import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Icon } from '@tviklax/ui-components';
import { AttentionItem } from './types';

const StripContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0;
  margin: 0 -4px; /* Slight bleed to show there is more content */
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LabelText = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.gray800};
  white-space: nowrap;
`;

const Pill = styled(motion.button) <{ $priority: 'urgent' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border-radius: 9999px;
  
  /* Severity Hierarchy */
  ${props => {
    switch (props.$priority) {
      case 'urgent':
        return `
          background-color: #dc2626;
          color: #ffffff;
          border: 1px solid #dc2626;
        `;
      case 'warning':
        return `
          background-color: transparent;
          color: #d97706;
          border: 1px solid #d97706;
        `;
      case 'info':
        return `
          background-color: transparent;
          color: #854d0e;
          border: 1px solid #eab308;
          opacity: 0.8;
        `;
      default:
        return '';
    }
  }}
  
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    ${props => {
    switch (props.$priority) {
      case 'urgent':
        return 'background-color: #b91c1c;';
      case 'warning':
        return 'background-color: #fffbeb;';
      case 'info':
        return 'background-color: #fefce8; opacity: 1;';
      default:
        return '';
    }
  }}
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Count = styled.span`
  font-weight: 700;
`;

const EmptyState = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.gray500};
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface AttentionRequiredStripProps {
  items: AttentionItem[];
}

export const AttentionRequiredStrip: React.FC<AttentionRequiredStripProps> = ({ items }) => {
  if (items.length === 0) {
    return (
      <EmptyState>
        <Icon name="CheckCircle" color="#10B981" />
        No attention required items today! 🎉
      </EmptyState>
    );
  }

  return (
    <StripContainer>
      <LabelText>TODAY / ATTENTION REQUIRED:</LabelText>
      {items.map((item, index) => (
        <Pill
          key={item.label}
          $priority={item.priority}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
        >
          {item.label} → <Count>{item.count}</Count>
        </Pill>
      ))}
    </StripContainer>
  );
};
